"use client";

import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import NextImage from 'next/image';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

type DevelopmentClientProps = {
    initialTranslations: Record<string, string>;
    initialCommonTranslations: Record<string, string>;
};

const fadeIn = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
};

const DevelopmentClient = ({
    initialTranslations,
    initialCommonTranslations,
}: DevelopmentClientProps) => {
    const { t, translationsLoaded } = useComponentTranslations('Development');
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const getInitialTranslation = useCallback((key: string, fallback?: string) => {
        return initialTranslations[key] ?? initialCommonTranslations[key] ?? fallback ?? key;
    }, [initialCommonTranslations, initialTranslations]);

    const et = useCallback((key: string, fallback?: string) => {
        if (!translationsLoaded) return getInitialTranslation(key, fallback);
        return t(key, fallback) ?? getInitialTranslation(key, fallback);
    }, [getInitialTranslation, t, translationsLoaded]);

    const services = useMemo(() => [
        { 
            title: et('devServiceSoftwareTitle'), 
            description: et('devServiceSoftwareDesc'), 
            detail: et('devServiceSoftwareDetail'),
            icon: 'carbon:application-web',
            image: '/images/robot-working-with-computer_1048-4282.jpg' 
        },
        { 
            title: et('devServiceCloudDevOpsTitle'), 
            description: et('devServiceCloudDevOpsDesc'), 
            detail: et('devServiceCloudDevOpsDetail'),
            icon: 'carbon:cloud',
            image: '/images/cloud.jpg' 
        },
        { 
            title: et('devServiceDataAnalyticsTitle'), 
            description: et('devServiceDataAnalyticsDesc'), 
            detail: et('devServiceDataAnalyticsDetail'),
            icon: 'carbon:data-vis-4',
            image: '/images/data.jpg' 
        },
        { 
            title: et('devServiceAiMlEngineeringTitle'), 
            description: et('devServiceAiMlEngineeringDesc'), 
            detail: et('devServiceAiMlEngineeringDetail'),
            icon: 'carbon:watson',
            image: '/images/ml.jpg' 
        },
        { 
            title: et('devServiceIotTitle'), 
            description: et('devServiceIotDesc'), 
            detail: et('devServiceIotDetail'),
            icon: 'carbon:iot',
            image: '/images/iot.jpg' 
        },
        { 
            title: et('devServiceLegacyModernizationTitle'), 
            description: et('devServiceLegacyModernizationDesc'), 
            detail: et('devServiceLegacyModernizationDetail'),
            icon: 'carbon:renew',
            image: '/images/robot-constructor_1048-4570.jpg' 
        },
    ], [et]);

    const cases = useMemo(() => [
        { title: et('devCase1Title'), result: et('devCase1Result'), desc: et('devCase1Desc'), image: '/images/retail.jpg' },
        { title: et('devCase2Title'), result: et('devCase2Result'), desc: et('devCase2Desc'), image: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: et('devCase3Title'), result: et('devCase3Result'), desc: et('devCase3Desc'), image: '/images/ml_d.jpg' },
    ], [et]);

    const pricingPlans = useMemo(() => [
        {
            name: et('devPrice1Name'),
            price: et('devPrice1Price'),
            desc: et('devPrice1Desc'),
            bullets: [et('devPrice1Bullet1'), et('devPrice1Bullet2'), et('devPrice1Bullet3'), et('devPrice1Bullet4')],
            icon: 'carbon:time',
            popular: false,
        },
        {
            name: et('devPrice2Name'),
            price: et('devPrice2Price'),
            desc: et('devPrice2Desc'),
            bullets: [et('devPrice2Bullet1'), et('devPrice2Bullet2'), et('devPrice2Bullet3'), et('devPrice2Bullet4')],
            icon: 'carbon:user-speaker',
            popular: true,
        },
        {
            name: et('devPrice3Name'),
            price: et('devPrice3Price'),
            desc: et('devPrice3Desc'),
            bullets: [et('devPrice3Bullet1'), et('devPrice3Bullet2'), et('devPrice3Bullet3'), et('devPrice3Bullet4')],
            icon: 'carbon:checkmark-outline',
            popular: false,
        },
    ], [et]);

    const processSteps = useMemo(() => [
        { step: et('devStep1'), title: et('devStep1Title'), desc: et('devStep1Desc') },
        { step: et('devStep2'), title: et('devStep2Title'), desc: et('devStep2Desc') },
        { step: et('devStep3'), title: et('devStep3Title'), desc: et('devStep3Desc') },
        { step: et('devStep4'), title: et('devStep4Title'), desc: et('devStep4Desc') },
    ], [et]);

    const technologies = [
        { name: 'React / Next.js', icon: 'carbon:logo-react' },
        { name: 'Node.js', icon: 'carbon:logo-node' },
        { name: 'Python', icon: 'carbon:logo-python' },
        { name: 'PostgreSQL', icon: 'carbon:data-base' },
        { name: 'Docker', icon: 'carbon:container-services' },
        { name: 'AWS / GCP', icon: 'carbon:cloud' },
        { name: 'Kubernetes', icon: 'carbon:kubernetes' },
        { name: 'TypeScript', icon: 'carbon:code' },
    ];

    const faqItems = useMemo(() => [
        { question: et('devFaq1q'), answer: et('devFaq1a') },
        { question: et('devFaq2q'), answer: et('devFaq2a') },
        { question: et('devFaq3q'), answer: et('devFaq3a') },
        { question: et('devFaq4q'), answer: et('devFaq4a') },
        { question: et('devFaq5q'), answer: et('devFaq5a') },
    ], [et]);

    return (
        <div className="relative min-h-screen bg-transparent text-white">
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-32 pb-16">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {et('developmentPageTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/60 max-w-2xl mx-auto mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    {et('developmentPageSubtitle')}
                </motion.p>
                <motion.p
                    className="text-sm text-white/50 max-w-3xl mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {et('developmentIntro')}
                </motion.p>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-16" {...fadeIn}>
                        {et('devServicesTitle')}
                    </motion.h2>
                    <div className="space-y-16">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="lg:w-1/2">
                                    <div className="glass-card glass-rounded overflow-hidden aspect-video relative">
                                        <NextImage 
                                            src={service.image} 
                                            alt={service.title} 
                                            fill
                                            className="object-cover" 
                                        />
                                    </div>
                                </div>
                                <div className="lg:w-1/2 space-y-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <Icon icon={service.icon} className="text-3xl text-blue-400" />
                                        <h3 className="text-2xl font-semibold text-white">{service.title}</h3>
                                    </div>
                                    <p className="text-white/70">{service.description}</p>
                                    <p className="text-white/50 text-sm">{service.detail}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="w-full py-24 glass-section glass-border-y my-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-16" {...fadeIn}>
                        {et('devCasesTitle')}
                    </motion.h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {cases.map((item, index) => (
                            <motion.div
                                key={index}
                                className="glass-card glass-rounded overflow-hidden glass-hover glass-transition"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                                <div className="relative h-48">
                                    <NextImage src={item.image} alt={item.title} fill className="object-cover" />
                                    <div className="absolute inset-0 bg-linear-to-t from-black/80 to-transparent" />
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <div className="text-blue-400 text-sm font-semibold mb-1">{item.result}</div>
                                        <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <p className="text-sm text-white/70 leading-normal">{item.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <motion.div className="text-center mb-16" {...fadeIn}>
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 mb-4">{et('devPricingTitle')}</h2>
                        <p className="text-white/70 max-w-xl mx-auto">{et('devPricingSubtitle')}</p>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {pricingPlans.map((plan, index) => (
                            <motion.div
                                key={index}
                                className={`glass-card glass-rounded p-8 glass-hover glass-transition relative ${plan.popular ? 'border-blue-400/50 border-2' : ''}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                                        Popular
                                    </div>
                                )}
                                <Icon icon={plan.icon} className="text-4xl text-blue-400 mb-4" />
                                <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                                <div className="text-2xl font-bold text-blue-400 mb-4">{plan.price}</div>
                                <p className="text-sm text-white/70 mb-6">{plan.desc}</p>
                                <ul className="space-y-3">
                                    {plan.bullets.map((bullet, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                                            <Icon icon="carbon:checkmark" className="text-blue-400 mt-0.5 shrink-0" />
                                            {bullet}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </section>

                <section className="my-24">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-16" {...fadeIn}>
                        {et('devProcessTitle')}
                    </motion.h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {processSteps.map((step, index) => (
                            <motion.div
                                key={index}
                                className="p-8 glass-border-strong glass-rounded glass-card"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                                <div className="text-5xl font-bold text-blue-400/30 mb-4">{step.step}</div>
                                <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                                <p className="text-white/70 leading-normal text-sm">{step.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </section>

                <section className="my-24">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-12" {...fadeIn}>
                        {et('devTechTitle')}
                    </motion.h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {technologies.map((tech, index) => (
                            <motion.div
                                key={index}
                                className="p-6 glass-border-strong glass-rounded glass-card flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                                <Icon icon={tech.icon} className="text-4xl mb-3 text-blue-400" />
                                <span className="text-sm font-medium text-white/70">{tech.name}</span>
                            </motion.div>
                        ))}
                    </div>
                </section>

                <section className="my-24 max-w-3xl mx-auto">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-12" {...fadeIn}>
                        {et('devFaqTitle')}
                    </motion.h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="glass-card glass-rounded overflow-hidden"
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                                <button
                                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                    className="w-full p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                                >
                                    <span className="font-medium pr-4">{item.question}</span>
                                    <Icon 
                                        icon={openFaq === index ? 'carbon:chevron-up' : 'carbon:chevron-down'} 
                                        className="text-xl text-blue-400 shrink-0" 
                                    />
                                </button>
                                {openFaq === index && (
                                    <div className="px-5 pb-5 text-white/60 text-sm leading-relaxed">
                                        {item.answer}
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </section>

                <IndustrialsForm label={et('devCtaTitle')} subtitle={et('devCtaSubtitle')} />
            </div>
        </div>
    );
};

export default DevelopmentClient;
